// frontend/src/pages/shops/ShopNotifications.js
// /dashboard/notifications — the business owner's notification list (R1/R2).
//
// This page did not exist. The bell and a "Notifications" menu entry were only on the professional and
// freelancer sidebars, so an owner had no way to read their own alerts at all — while the shop's notices
// (trial ending, subscription, support imports) were being written to their account.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";

const TABS = [
  { key: "all", label: "All" },
  { key: "booking", label: "Booking" },
  { key: "cancellation", label: "Cancellation" },
  { key: "system", label: "System" },
  { key: "message", label: "Message" },
];

const ICONS = {
  booking: "📅",
  cancellation: "🚫",
  message: "💬",
  review: "⭐",
  package_created: "📦",
  package_updated: "🔄",
  system: "⚙️",
  inventory_alert: "📉",
  low_stock: "📉",
  stock_alert: "📉",
};

function timeAgo(value) {
  if (!value) return "";
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(value).toLocaleDateString();
}

function ShopNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterType, setFilterType] = useState("all");

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const fetchNotifications = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/api/notifications`, {
        headers,
        params: { limit: 100 },
      });
      setNotifications(response.data?.notifications || []);
      setErrorMessage("");
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
        return;
      }
      setErrorMessage(err?.response?.data?.message || "Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, authHeaders, navigate]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    const headers = authHeaders();
    if (!headers) return;
    setNotifications((prev) =>
      prev.map((item) => (item._id === id ? { ...item, read: true } : item)),
    );
    try {
      await axios.patch(`${apiBaseUrl}/api/notifications/${id}/read`, {}, { headers });
    } catch (err) {
      // The badge will correct itself on the next fetch.
    }
  };

  const markAllAsRead = async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.post(`${apiBaseUrl}/api/notifications/mark-all-read`, {}, { headers });
      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    } catch (err) {
      setErrorMessage("Unable to mark notifications as read.");
    }
  };

  const openNotification = async (notification) => {
    if (!notification.read) await markAsRead(notification._id);
    const url = String(notification.actionUrl || "");
    // Only in-app destinations; an arbitrary URL in a stored notification must not redirect the owner away.
    if (url.startsWith("/dashboard/")) navigate(url);
  };

  const visible =
    filterType === "all"
      ? notifications
      : notifications.filter((item) => item.type === filterType);

  const unread = notifications.filter((item) => !item.read).length;

  return (
    <SidebarLayout>
      <div style={{ padding: "24px", maxWidth: 900, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 style={{ margin: 0 }}>Notifications</h1>
            <p style={{ color: "#6b7280", marginTop: 6 }}>
              Booking activity, subscription notices and anything the shop needs to act on.
            </p>
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllAsRead}
              style={{
                background: "#00509d",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "9px 14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Mark all read ({unread})
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "18px 0" }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilterType(tab.key)}
              style={{
                padding: "7px 14px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
                background: filterType === tab.key ? "#00509d" : "#fff",
                color: filterType === tab.key ? "#fff" : "#374151",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {errorMessage && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 16,
            }}
          >
            {errorMessage}
          </div>
        )}

        {loading ? (
          <p style={{ color: "#6b7280" }}>Loading…</p>
        ) : visible.length === 0 ? (
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 40,
              textAlign: "center",
              color: "#9ca3af",
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔔</div>
            <div>No notifications{filterType === "all" ? " yet" : " in this category"}.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {visible.map((notification) => (
              <button
                key={notification._id}
                type="button"
                onClick={() => openNotification(notification)}
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                  textAlign: "left",
                  width: "100%",
                  background: notification.read ? "#fff" : "#f0f9ff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: "14px 16px",
                  cursor: notification.actionUrl ? "pointer" : "default",
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1.2 }}>
                  {ICONS[notification.type] || "🔔"}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontWeight: notification.read ? 500 : 700,
                      color: "#111827",
                      marginBottom: 4,
                    }}
                  >
                    {notification.title || "Notification"}
                  </span>
                  {notification.message && (
                    <span
                      style={{
                        display: "block",
                        fontSize: 13.5,
                        color: "#4b5563",
                        lineHeight: 1.5,
                      }}
                    >
                      {notification.message}
                    </span>
                  )}
                  <span style={{ display: "block", fontSize: 12, color: "#9ca3af", marginTop: 6 }}>
                    {timeAgo(notification.createdAt)}
                  </span>
                </span>
                {!notification.read && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#3b82f6",
                      flexShrink: 0,
                      marginTop: 6,
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default ShopNotifications;
